import React, { FC, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { Node } from 'src/common/interfaces/node.interface';
import { getContentMetadata } from 'src/utils/journey';
import { DeleteStrongIcon, EditStrongIcon } from 'src/monet/icons';
import { IconButton } from 'src/common/icon-button';

import { useStepEditorContext } from 'src/store/step-editor';
import { EventBus } from 'src/common/EventBus';
import { StepEditorWarningPanel } from '../warning-panel';
import { functionNoop } from 'src/utils/function/noop';
import { STEP_EDITOR_CONTENT_OPTION_URL } from 'src/editor/constants';
import { ContentTypeLogo } from 'src/dashboard/ContentTypeLogo';
import contentEditable from 'src/utils/contentEditable';

interface StepEditorContentPreviewProps {
  node?: Node;
  onDelete?: () => void;
  onEditClick?: () => void;
  onContentNameUpdate: (name: string) => any;
}

export const StepEditorContentPreview: FC<StepEditorContentPreviewProps> = ({
  node,
  onDelete,
  onEditClick,
  onContentNameUpdate,
}) => {
  const warningPanelOpenRef = useRef(functionNoop);
  const warningPanelCloseRef = useRef(functionNoop);
  const {
    url,
    contentName,
    destination,
    contentOption,
    contentType,
    video_asset,
    file_asset,
    image_asset,
    link,
    thumbnail,
  } = useStepEditorContext();
  const [forceUpdateKey, forceUpdate] = useReducer((x) => x + 1, 0);

  const filePreviewParams = useMemo(() => {
    let previewUrl = url;
    if (video_asset) {
      previewUrl = video_asset.url;
    } else if (file_asset) {
      previewUrl = file_asset.url;
    } else if (image_asset) {
      previewUrl = image_asset.url;
    } else if (link) {
      previewUrl = url;
    }

    return {
      url: previewUrl,
      meta: getContentMetadata(node) || url,
      thumbnailUrl: thumbnail ? thumbnail.url : null,
    };
  }, [url, file_asset, video_asset, image_asset, link, node, thumbnail]);

  const onWarningPanelConfirmed = useCallback(() => {
    EventBus.emit('editor:reset-content');
  }, [contentName]);

  useEffect(() => {
    const onEditorShowWarningContentPanel = () => {
      warningPanelOpenRef.current();
    };

    const onEditorRenderResetContentWarningPanel = () => {
      forceUpdate();
    };

    EventBus.on('editor:render-reset-content-warning-panel', onEditorRenderResetContentWarningPanel);
    EventBus.on('editor:show-warning-content-panel', onEditorShowWarningContentPanel);
    return () => {
      EventBus.off('editor:render-reset-content-warning-panel', onEditorRenderResetContentWarningPanel);
      EventBus.off('editor:show-warning-content-panel', onEditorShowWarningContentPanel);
    };
  }, []);

  const onWarningPanelDenied = useCallback(() => {
    warningPanelCloseRef.current();
  }, []);

  const EditableContentName = contentEditable('div');

  return (
    <div className='relative flex items-start justify-between'>
      <div className='relative flex justify-start items-start rounded-md max-w-[312px]'>
        <ContentTypeLogo destination={destination} contentType={contentType} externalLinkUrl={url} />
        <div className='flex flex-col ml-2 min-w-0 flex-grow-0 my-auto'>
          <div className='text-bedrock-p-strong text-bedrock-black' style={{ wordBreak: 'break-word' }}>
            <EditableContentName
              data-placeholder={'Name this content'}
              value={contentName || 'Name this content'}
              disableAutoFocus={true}
              className='text-bedrock-p-strong text-bedrock-p'
              onSave={(prevValue, newValue) => onContentNameUpdate(newValue)}
            />
          </div>
          {filePreviewParams.meta && (
            <>
              {contentOption !== STEP_EDITOR_CONTENT_OPTION_URL ? (
                <p className='text-bedrock-p-small text-bedrock-dark-gray break-all truncate line-clamp-1'>
                  {filePreviewParams.meta}
                </p>
              ) : (
                <a
                  className='text-bedrock-p-small has-hover text-bedrock-dark-gray break-all line-clamp-1 hover:underline'
                  href={url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {url}
                </a>
              )}
            </>
          )}
        </div>
      </div>
      {onDelete && (
        <button type='button' className='text-bedrock-black hover:opacity-80' onClick={onDelete}>
          <DeleteStrongIcon />
        </button>
      )}
      {onEditClick && (
        <StepEditorWarningPanel
          key={`content-warning-panel-${forceUpdateKey}`}
          onConfirm={onWarningPanelConfirmed}
          openRef={warningPanelOpenRef}
          closeRef={warningPanelCloseRef}
          onDeny={onWarningPanelDenied}
          copy={
            <span className='text-bedrock-p text-bedrock-black'>
              Would you like to change this content to something else? Everything else will remain.
            </span>
          }
          confirmCopy='Yes, change'
          denyCopy='No, keep'
          renderOrigin={() => (
            <IconButton>
              <EditStrongIcon />
            </IconButton>
          )}
        />
      )}
    </div>
  );
};
